import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  Edit,
  AddTask,
  CheckCircle,
  AutoAwesome,
  Delete,
  Save,
  Cancel,
  ExpandMore,
  Undo,
  WarningAmber,
  DeviceHub,
  ReportProblem,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { createTickets } from '../../store/strategistSlice';
import { updateAgentOutput } from '../../store/strategistSlice';
import { getBreakdownNodeType, renderBreakdownHierarchy } from './strategistAgentUtils';
import { toast } from 'react-toastify';
import { AGENT_OUTPUT_LEVELS } from '../../shared/appConstants';
import { normalizeRichTextHtml } from '../../shared/htmlUtils';
import './BreakdownPreview.scss';

const BreakdownPreview = ({
  breakdown,
  projectId,
  onEdit,
  onTicketsCreated,
  outputLevel = 'epic',
  ticketsGenerated = false,
  agentLogId,
  onRefresh,
  breakdownHistoryId,
  insights = null,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.strategist);
  const { loading: strategistLoading } = useSelector((state) => state.strategist);

  const [expanded, setExpanded] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [editedItems, setEditedItems] = useState({});
  const [deletedItems, setDeletedItems] = useState(new Set());
  const [localBreakdown, setLocalBreakdown] = useState(breakdown);
  const [hasChanges, setHasChanges] = useState(false);
  const [isGlobalEditMode, setIsGlobalEditMode] = useState(false);

  // Update local breakdown when prop changes
  React.useEffect(() => {
    setLocalBreakdown(breakdown);
    setHasChanges(false);
    setDeletedItems(new Set());
    setEditedItems({});
  }, [breakdown]);

  // Get unique path for an item - use stable ID that doesn't change with content
  const getItemPath = (node, level = 0, parentType = null, index = 0, parentPath = '') => {
    const basePath = parentPath ? `${parentPath}-${index}` : `${level}-${parentType || 'root'}-${index}`;
    return basePath;
  };

  const handleAccordionChange = (nodeId) => {
    setExpanded((prev) =>
      prev.includes(nodeId) ? prev.filter((id) => id !== nodeId) : [...prev, nodeId]
    );
  };

  // Count items by type in the breakdown (excluding deleted items) - for nested items
  const countItemsByType = useCallback((breakdownData, type) => {
    let count = 0;
    
    const countInNode = (node, nodeLevel, nodeParentType, nodeIndex, parentPath = '') => {
      const nodeId = getItemPath(node, nodeLevel, nodeParentType, nodeIndex, parentPath);
      
      // Determine node type based on level and parent type
      let nodeType = 'subtask'; // default
      if (nodeLevel === 0) nodeType = 'epic';
      else if (nodeParentType === 'epic') nodeType = 'feature';
      else if (nodeParentType === 'feature') nodeType = 'task';
      else if (nodeParentType === 'task') nodeType = 'subtask';
      
      // Only count if not deleted and matches type
      if (nodeType === type && !deletedItems.has(nodeId)) {
        count++;
      }
      
      if (node.features) {
        node.features.forEach((feature, idx) => countInNode(feature, nodeLevel + 1, 'epic', idx, `${nodeId}-features`));
      }
      if (node.tasks) {
        node.tasks.forEach((task, idx) => countInNode(task, nodeLevel + 1, 'feature', idx, `${nodeId}-tasks`));
      }
      if (node.subtasks) {
        node.subtasks.forEach((subtask, idx) => countInNode(subtask, nodeLevel + 1, 'task', idx, `${nodeId}-subtasks`));
      }
    };

    if (breakdownData) {
      if (breakdownData.epic) {
        countInNode(breakdownData.epic, 0, null, 0);
      } else {
        if (breakdownData.epics) {
          breakdownData.epics.forEach((epic, idx) => countInNode(epic, 0, null, idx));
        }
        if (breakdownData.features) {
          breakdownData.features.forEach((feature, idx) => countInNode(feature, 0, null, idx, 'root-features'));
        }
        if (breakdownData.tasks) {
          breakdownData.tasks.forEach((task, idx) => countInNode(task, 0, null, idx, 'root-tasks'));
        }
        if (breakdownData.subtasks) {
          breakdownData.subtasks.forEach((subtask, idx) => countInNode(subtask, 0, null, idx, 'root-subtasks'));
        }
      }
    }
    
    return count;
  }, [deletedItems]);

  // Check if item can be deleted
  // Simple rule: cannot delete if there's only one item of that type (anywhere in the breakdown)
  const canDeleteItem = (nodeType, breakdownData, nodeId) => {
    const countAllByType = countItemsByType(breakdownData, nodeType);
    return countAllByType > 1;
  };

  // Handle field change in edit mode
  const handleFieldChange = (itemPath, field, value) => {
    setEditedItems((prev) => {
      const currentItem = prev[itemPath] || {};
      const newState = {
        ...prev,
        [itemPath]: {
          ...currentItem,
          [field]: value,
        },
      };
      return newState;
    });
    // Only set hasChanges if in global edit mode
    if (isGlobalEditMode) {
      setHasChanges(true);
    }
  };

  // Handle edit start
  const handleStartEdit = (itemPath, node) => {
    if (!isGlobalEditMode) {
      setEditingItem(itemPath);
    }
    setEditedItems((prev) => ({
      ...prev,
      [itemPath]: {
        title: node.epic || node.feature || node.task || node.title || '',
        description: node.description || '',
      },
    }));
  };

  // Handle global edit cancel
  const handleGlobalEditCancel = () => {
    setEditedItems({});
    setEditingItem(null);
    setHasChanges(false);
    setDeletedItems(new Set());
    setIsGlobalEditMode(false);
  };

  // Handle global edit mode toggle
  const handleGlobalEditToggle = () => {
    // Entering global edit mode - prepare all items for editing and expand all
    const allItems = {};
    const allNodeIds = [];
    const prepareItems = (node, level = 0, parentType = null, index = 0, parentPath = '') => {
      const nodeId = getItemPath(node, level, parentType, index, parentPath);
      allNodeIds.push(nodeId);
      if (!deletedItems.has(nodeId)) {
        allItems[nodeId] = {
          title: node.epic || node.feature || node.task || node.title || '',
          description: node.description || '',
        };
      }
      if (node.features) {
        node.features.forEach((feature, idx) => prepareItems(feature, level + 1, 'epic', idx, `${nodeId}-features`));
      }
      if (node.tasks) {
        node.tasks.forEach((task, idx) => prepareItems(task, level + 1, 'feature', idx, `${nodeId}-tasks`));
      }
      if (node.subtasks) {
        node.subtasks.forEach((subtask, idx) => prepareItems(subtask, level + 1, 'task', idx, `${nodeId}-subtasks`));
      }
    };
    
    if (localBreakdown) {
      if (localBreakdown.epic) {
        prepareItems(localBreakdown.epic, 0, null, 0);
      } else {
        if (localBreakdown.features) {
          localBreakdown.features.forEach((feature, idx) => prepareItems(feature, 0, null, idx, 'root-features'));
        }
        if (localBreakdown.tasks) {
          localBreakdown.tasks.forEach((task, idx) => prepareItems(task, 0, null, idx, 'root-tasks'));
        }
        if (localBreakdown.subtasks) {
          localBreakdown.subtasks.forEach((subtask, idx) => prepareItems(subtask, 0, null, idx, 'root-subtasks'));
        }
      }
    }
    setEditedItems(allItems);
    setExpanded(allNodeIds); // Expand all accordions
    setHasChanges(true);
    setIsGlobalEditMode(true);
  };

  // Handle cancel edit
  const handleCancelEdit = (itemPath) => {
    setEditingItem(null);
    setEditedItems((prev) => {
      const newState = { ...prev };
      delete newState[itemPath];
      return newState;
    });
  };

  // Handle save edit - immediate API call for individual edits
  const handleSaveEdit = async (itemPath, node, level, parentType, index) => {
    const edited = editedItems[itemPath];
    if (!edited) return;

    // Create updated breakdown by directly applying the single edit
    const updatedBreakdown = JSON.parse(JSON.stringify(localBreakdown)); // Deep clone
    
    // Find and update the specific node
    const findAndUpdate = (obj, targetPath) => {
      const traverse = (node, currentLevel = 0, currentParentType = null, currentIndex = 0, parentPath = '') => {
        const currentNodeId = getItemPath(node, currentLevel, currentParentType, currentIndex, parentPath);
        
        if (currentNodeId === targetPath) {
          // Update the node directly
          if (node.epic !== undefined) {
            node.epic = edited.title;
          } else if (node.feature !== undefined) {
            node.feature = edited.title;
          } else if (node.task !== undefined) {
            node.task = edited.title;
          } else if (node.title !== undefined) {
            node.title = edited.title;
          }
          node.description = edited.description;
          return true; // Found and updated
        }
        
        // Traverse children
        if (node.features) {
          for (let i = 0; i < node.features.length; i++) {
            if (traverse(node.features[i], currentLevel + 1, 'epic', i, `${currentNodeId}-features`)) {
              return true;
            }
          }
        }
        if (node.tasks) {
          for (let i = 0; i < node.tasks.length; i++) {
            if (traverse(node.tasks[i], currentLevel + 1, 'feature', i, `${currentNodeId}-tasks`)) {
              return true;
            }
          }
        }
        if (node.subtasks) {
          for (let i = 0; i < node.subtasks.length; i++) {
            if (traverse(node.subtasks[i], currentLevel + 1, 'task', i, `${currentNodeId}-subtasks`)) {
              return true;
            }
          }
        }
        
        return false;
      };
      
      // Start traversal - check ALL possible arrays
      if (obj.epic) {
        traverse(obj.epic, 0, null, 0, 'root-epic');
      }
      if (obj.epics) {
        for (let i = 0; i < obj.epics.length; i++) {
          if (traverse(obj.epics[i], 0, null, i, 'root-epics')) break;
        }
      }
      if (obj.features) {
        for (let i = 0; i < obj.features.length; i++) {
          if (traverse(obj.features[i], 0, null, i, 'root-features')) break;
        }
      }
      if (obj.tasks) {
        for (let i = 0; i < obj.tasks.length; i++) {
          if (traverse(obj.tasks[i], 0, null, i, 'root-tasks')) break;
        }
      }
      if (obj.subtasks) {
        for (let i = 0; i < obj.subtasks.length; i++) {
          if (traverse(obj.subtasks[i], 0, null, i, 'root-subtasks')) break;
        }
      }
    };
    
    findAndUpdate(updatedBreakdown, itemPath);
    const filteredBreakdown = filterDeleted(updatedBreakdown);


    // Save immediately for individual edits
    if (agentLogId) {
      try {
        await dispatch(updateAgentOutput({
          agentLogId,
          agentOutput: filteredBreakdown
        })).unwrap();
        
        // Call GET API to refresh data
        if (onRefresh) {
          await onRefresh();
        }
        
        // Reset states after successful save - only clear the specific item
        setEditingItem(null);
        setEditedItems((prev) => {
          const newState = { ...prev };
          delete newState[itemPath];
          return newState;
        });
        // Don't set hasChanges to false for individual saves
        
        toast.success(t('agent.breakdown.changesSaved'));
      } catch (err) {
        toast.error(t('agent.breakdown.saveChangesFailed'));
      }
    }
  };

  // Helper function to filter deleted items - handles root level properly
  const filterDeleted = (breakdown) => {
    const filterNode = (node, level = 0, parentType = null, index = 0, parentPath = '') => {
      const nodeId = getItemPath(node, level, parentType, index, parentPath);
      if (deletedItems.has(nodeId)) {
        return null;
      }
      const filtered = { ...node };
      if (filtered.features) {
        filtered.features = filtered.features
          .map((feature, idx) => filterNode(feature, level + 1, 'epic', idx, `${nodeId}-features`))
          .filter(Boolean);
      }
      if (filtered.tasks) {
        filtered.tasks = filtered.tasks
          .map((task, idx) => filterNode(task, level + 1, 'feature', idx, `${nodeId}-tasks`))
          .filter(Boolean);
      }
      if (filtered.subtasks) {
        filtered.subtasks = filtered.subtasks
          .map((subtask, idx) => filterNode(subtask, level + 1, 'task', idx, `${nodeId}-subtasks`))
          .filter(Boolean);
      }
      return filtered;
    };

    const result = { ...breakdown };
    
    if (result.epic) {
      result.epic = filterNode(result.epic, 0, null, 0, 'root-epic');
      if (!result.epic) delete result.epic;
    }
    
    if (result.epics) {
      result.epics = result.epics
        .map((epic, idx) => filterNode(epic, 0, null, idx, 'root-epics'))
        .filter(Boolean);
    }
    
    if (result.features) {
      result.features = result.features
        .map((feature, idx) => filterNode(feature, 0, null, idx, 'root-features'))
        .filter(Boolean);
    }
    
    if (result.tasks) {
      result.tasks = result.tasks
        .map((task, idx) => filterNode(task, 0, null, idx, 'root-tasks'))
        .filter(Boolean);
    }
    
    if (result.subtasks) {
      result.subtasks = result.subtasks
        .map((subtask, idx) => filterNode(subtask, 0, null, idx, 'root-subtasks'))
        .filter(Boolean);
    }
    
    return result;
  };

  // Handle delete - just mark as deleted, don't call API
  const handleDelete = (itemPath, nodeType) => {
    const breakdownData = localBreakdown;
    if (!canDeleteItem(nodeType, breakdownData, itemPath)) {
      toast.error(t('agent.breakdown.cannotDeleteLastItem', { type: nodeType }));
      return;
    }
    
    setDeletedItems((prev) => new Set([...prev, itemPath]));
    if (isGlobalEditMode) {
      setHasChanges(true);
    } else {
      // For individual delete, set as editing item to show save/cancel options
      setEditingItem(itemPath);
    }
  };

  // Handle restore
  const handleRestore = (itemPath) => {
    setDeletedItems((prev) => {
      const newSet = new Set(prev);
      newSet.delete(itemPath);
      return newSet;
    });
    if (isGlobalEditMode) {
      setHasChanges(true);
    }
  };

  // Handle individual delete save
  const handleDeleteSave = async (itemPath) => {
    const updatedBreakdown = JSON.parse(JSON.stringify(localBreakdown));
    const filteredBreakdown = filterDeleted(updatedBreakdown);

    if (agentLogId) {
      try {
        await dispatch(updateAgentOutput({
          agentLogId,
          agentOutput: filteredBreakdown
        })).unwrap();
        
        if (onRefresh) {
          await onRefresh();
        }
        
        setEditingItem(null);
        setDeletedItems(new Set());
        
        toast.success(t('agent.breakdown.changesSaved'));
      } catch (err) {
        toast.error(t('agent.breakdown.saveChangesFailed'));
      }
    }
  };

  // Handle individual delete cancel
  const handleDeleteCancel = (itemPath) => {
    setDeletedItems((prev) => {
      const newSet = new Set(prev);
      newSet.delete(itemPath);
      return newSet;
    });
    setEditingItem(null);
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    const pName = typeof priority === 'object' ? priority.name : priority;
    switch (pName?.toLowerCase()) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const renderTreeItem = (node, level = 0, parentType = null, index = 0, context = null) => {
    // Generate parentPath based on context for root items, or use hierarchical path for nested items
    const parentPath = context || '';
    const nodeId = getItemPath(node, level, parentType, index, parentPath);
    
    // Use the utility function for node type detection
    const nodeType = getBreakdownNodeType(node, level, parentType, context);
    
    const nodeDescription = node.description || '';
    const isDeleted = deletedItems.has(nodeId);
    const isEditing = (isGlobalEditMode || editingItem === nodeId) && !deletedItems.has(nodeId);
    const editedNode = editedItems[nodeId];
    const displayTitle = editedNode ? editedNode.title : (node.epic || node.feature || node.task || node.title || '');
    const displayDescription = editedNode ? editedNode.description : (node.description || '');

    const hasChildren =
      (node.features && node.features.length > 0) ||
      (node.tasks && node.tasks.length > 0) ||
      (node.subtasks && node.subtasks.length > 0);

    const isExpanded = expanded.includes(nodeId);
    const hasDetails = nodeDescription || node.acceptanceCriteria || node.priority;
    const canDelete = canDeleteItem(nodeType, localBreakdown, nodeId);

    return (
      <Box 
        key={nodeId} 
        className="breakdown-tree-item"
        data-level={level}
        data-deleted={isDeleted && !(editingItem === nodeId)}
        style={{ marginLeft: `${level * 5}px`, position: 'relative' }}
      >
        <Paper
          elevation={level === 0 ? 2 : 1}
          className="breakdown-tree-item-paper"
          data-level={level}
          data-type={nodeType}
          sx={editingItem === nodeId && deletedItems.has(nodeId) ? { opacity: 0.6 } : {}}
        >
          <Box 
            className="breakdown-tree-item-content"
            data-has-details={hasDetails || hasChildren}
            onClick={hasChildren && !isDeleted ? () => handleAccordionChange(nodeId) : undefined}
            style={hasChildren && !isDeleted ? { cursor: 'pointer' } : {}}
          >
            <Box className="breakdown-tree-item-left">
              <Box className="breakdown-tree-item-header">
                <Box className="breakdown-tree-item-header-left">
                  <Chip
                    label={nodeType.charAt(0).toUpperCase() + nodeType.slice(1)}
                    size="small"
                    className={`breakdown-tree-item-type-chip breakdown-tree-item-type-chip-${nodeType}`}
                  />
                  {isEditing ? (
                    <TextField
                      value={displayTitle}
                      onChange={(e) => {
                        handleFieldChange(nodeId, 'title', e.target.value);
                      }}
                      size="small"
                      fullWidth
                      className="breakdown-tree-item-title-input"
                    />
                  ) : (
                    <Typography 
                      variant="h6" 
                      className="breakdown-tree-item-title"
                      data-level={level}
                    >
                      {displayTitle}
                    </Typography>
                  )}
                </Box>
                {editingItem === nodeId && deletedItems.has(nodeId) && (
                  <></>  
                )}
                {isEditing && !isGlobalEditMode && (
                  <Box className="breakdown-tree-item-header-actions">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Cancel />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCancelEdit(nodeId);
                      }}
                    >
                      {t('agent.breakdown.cancel')}
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Save />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSaveEdit(nodeId, node, level, parentType, index);
                      }}
                    >
                      {t('agent.breakdown.save')}
                    </Button>
                  </Box>
                )}
              </Box>

                {isEditing ? (
                <Box className="breakdown-tree-item-edit-section">
                  <Typography variant="subtitle2" className="breakdown-tree-item-edit-description-label">
                    {t('agent.breakdown.description')}
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={6}
                    value={displayDescription}
                    onChange={(e) => {
                      handleFieldChange(nodeId, 'description', e.target.value);
                    }}
                    size="small"
                    className="breakdown-tree-item-edit-description-field"
                  />
                </Box>
              ) : (
                <>
                  {nodeDescription && (
                    <Typography variant="body2" color="text.secondary" className="breakdown-tree-item-description">
                      {nodeDescription}
                    </Typography>
                  )}

                  {(node.priority || node.acceptanceCriteria) && (
                    <Box className="breakdown-tree-item-chips">
                      {node.priority && (
                        <Chip 
                          label={`${t('agent.breakdown.priority')}: ${typeof node.priority === 'object' ? node.priority.name : node.priority}`} 
                          size="small" 
                          variant="outlined"
                          color={typeof node.priority === 'object' && node.priority.colour ? undefined : getPriorityColor(node.priority)}
                          sx={typeof node.priority === 'object' && node.priority.colour ? { borderColor: node.priority.colour, color: node.priority.colour } : undefined}
                        />
                      )}
                      {node.acceptanceCriteria && (
                        <Chip
                          label={t('agent.breakdown.hasAC')}
                          size="small"
                          icon={<CheckCircle />}
                          color="success"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  )}

                  {node.acceptanceCriteria && isExpanded && (
                    <Box className="breakdown-tree-item-ac-section">
                      <Typography variant="subtitle2" className="breakdown-tree-item-ac-title">
                        {t('agent.breakdown.hasAC')}:
                      </Typography>
                      {node.acceptanceCriteria.criteria?.map((criterion, idx) => (
                        <Typography key={idx} variant="body2" className="breakdown-tree-item-ac-criterion">
                          {criterion.given && `Given: ${criterion.given}`}
                          {criterion.when && ` When: ${criterion.when}`}
                          {criterion.then && ` Then: ${criterion.then}`}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </>
              )}
            </Box>

            <Box className="breakdown-tree-item-actions">
              {!isDeleted && !isEditing && !(editingItem === nodeId && deletedItems.has(nodeId)) && (!ticketsGenerated || isGlobalEditMode) && (
                <>
                  <Tooltip 
                    title={t('agent.breakdown.edit')}
                    slotProps={{ tooltip: { className: 'app-tooltip' } }}
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStartEdit(nodeId, node);
                      }}
                      className="breakdown-tree-item-action-button"
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {canDelete && (
                    <Tooltip 
                      title={t('agent.breakdown.delete')}
                      slotProps={{ tooltip: { className: 'app-tooltip' } }}
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(nodeId, nodeType);
                        }}
                        className="breakdown-tree-item-delete-button"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              )}
              {!isDeleted && isGlobalEditMode && canDelete && (
                <Tooltip 
                  title={t('agent.breakdown.delete')}
                  slotProps={{ tooltip: { className: 'app-tooltip' } }}
                >
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(nodeId, nodeType);
                    }}
                    className="breakdown-tree-item-delete-button"
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {isDeleted && isGlobalEditMode && (
                <Tooltip 
                  title={t('agent.breakdown.restore')}
                  slotProps={{ tooltip: { className: 'app-tooltip' } }}
                >
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRestore(nodeId);
                    }}
                    className="breakdown-tree-item-restore-button"
                  >
                    <Undo fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {hasChildren && !isDeleted && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAccordionChange(nodeId);
                  }}
                  className="breakdown-tree-item-action-button"
                >
                  <ExpandMore 
                    className="breakdown-tree-item-expand-icon"
                    data-expanded={isExpanded}
                  />
                </IconButton>
              )}
            </Box>
          </Box>

          {isExpanded && hasChildren && !isDeleted && (
            <Box className="breakdown-tree-item-children">
              {node.features?.map((feature, idx) => renderTreeItem(feature, level + 1, 'epic', idx, `${nodeId}-features`))}
              {node.tasks?.map((task, idx) => renderTreeItem(task, level + 1, 'feature', idx, `${nodeId}-tasks`))}
              {node.subtasks?.map((subtask, idx) => renderTreeItem(subtask, level + 1, 'task', idx, `${nodeId}-subtasks`))}
            </Box>
          )}
        </Paper>
        {editingItem === nodeId && deletedItems.has(nodeId) && (
          <Box 
            sx={{ 
              position: 'absolute', 
              top: '7px', 
              right: '16px', 
              display: 'flex', 
              gap: 1,
              zIndex: 10
            }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<Cancel />}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteCancel(nodeId);
              }}
            >
              {t('agent.breakdown.cancel')}
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<Delete />}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteSave(nodeId);
              }}
              color="error"
            >
              {t('agent.breakdown.delete')}
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  // Handle save changes - apply all edits and deletions
  const handleSaveChanges = async () => {
    if (!agentLogId || !hasChanges) return;

    // Create updated breakdown by directly applying all edits
    const updatedBreakdown = JSON.parse(JSON.stringify(localBreakdown)); // Deep clone
    
    // Apply all edits
    const applyAllEdits = (obj) => {
      const traverse = (node, currentLevel = 0, currentParentType = null, currentIndex = 0, parentPath = '') => {
        const currentNodeId = getItemPath(node, currentLevel, currentParentType, currentIndex, parentPath);
        
        if (editedItems[currentNodeId]) {
          const edited = editedItems[currentNodeId];
          
          // Update the node directly
          if (node.epic !== undefined) {
            node.epic = edited.title;
          } else if (node.feature !== undefined) {
            node.feature = edited.title;
          } else if (node.task !== undefined) {
            node.task = edited.title;
          } else if (node.title !== undefined) {
            node.title = edited.title;
          }
          node.description = edited.description;
        }
        
        // Traverse children
        if (node.features) {
          for (let i = 0; i < node.features.length; i++) {
            traverse(node.features[i], currentLevel + 1, 'epic', i, `${currentNodeId}-features`);
          }
        }
        if (node.tasks) {
          for (let i = 0; i < node.tasks.length; i++) {
            traverse(node.tasks[i], currentLevel + 1, 'feature', i, `${currentNodeId}-tasks`);
          }
        }
        if (node.subtasks) {
          for (let i = 0; i < node.subtasks.length; i++) {
            traverse(node.subtasks[i], currentLevel + 1, 'task', i, `${currentNodeId}-subtasks`);
          }
        }
      };
      
      // Start traversal - check ALL possible arrays
      if (obj.epic) {
        traverse(obj.epic, 0, null, 0, 'root-epic');
      }
      if (obj.epics) {
        for (let i = 0; i < obj.epics.length; i++) {
          traverse(obj.epics[i], 0, null, i, 'root-epics');
        }
      }
      if (obj.features) {
        for (let i = 0; i < obj.features.length; i++) {
          traverse(obj.features[i], 0, null, i, 'root-features');
        }
      }
      if (obj.tasks) {
        for (let i = 0; i < obj.tasks.length; i++) {
          traverse(obj.tasks[i], 0, null, i, 'root-tasks');
        }
      }
      if (obj.subtasks) {
        for (let i = 0; i < obj.subtasks.length; i++) {
          traverse(obj.subtasks[i], 0, null, i, 'root-subtasks');
        }
      }
    };
    
    applyAllEdits(updatedBreakdown);
    const filteredBreakdown = filterDeleted(updatedBreakdown);

    try {
      await dispatch(updateAgentOutput({
        agentLogId,
        agentOutput: filteredBreakdown
      })).unwrap();
      
      // Call GET API to refresh data
      if (onRefresh) {
        await onRefresh();
      }
      
      // Reset all states after successful save
      setHasChanges(false);
      setEditedItems({});
      setDeletedItems(new Set());
      setIsGlobalEditMode(false);
      setEditingItem(null);
      
      toast.success(t('agent.breakdown.changesSaved'));
    } catch (err) {
      toast.error(t('agent.breakdown.saveChangesFailed'));
      console.error('Failed to save changes:', err);
    }
  };

  const handleCreateTickets = async () => {
    
    if (!localBreakdown || !projectId || !breakdownHistoryId) {
      return;
    }

    // Convert breakdown to the required API format
    const convertToAPIFormat = (breakdown) => {
      const epics = [];
      
      // Helper to convert a node to API format
      const convertNode = (node, nodeType) => {
        const title = node[nodeType] || node.title || `Untitled ${nodeType.charAt(0).toUpperCase() + nodeType.slice(1)}`;
        // Convert plain text with \n to HTML format, then normalize
        const rawDescription = node.description || '';
        const htmlDescription = rawDescription.replace(/\n/g, '<br>');
        const description = normalizeRichTextHtml(htmlDescription);
        
        const result = { title, description };
        
        // Add all possible nested children - API supports skipped hierarchy
        if (node.features) {
          result.features = node.features.map(f => convertNode(f, 'feature'));
        }
        if (node.tasks) {
          result.tasks = node.tasks.map(t => convertNode(t, 'task'));
        }
        if (node.subtasks) {
          result.subtasks = node.subtasks.map(s => convertNode(s, 'subtask'));
        }
        
        return result;
      };
      
      // Handle different root structures
      if (breakdown.epic) {
        epics.push(convertNode(breakdown.epic, 'epic'));
      } else if (breakdown.epics && breakdown.epics.length > 0) {
        breakdown.epics.forEach(epic => epics.push(convertNode(epic, 'epic')));
      } else if (breakdown.feature) {
        epics.push({
          title: 'Generated Epic',
          description: '',
          features: [convertNode(breakdown.feature, 'feature')]
        });
      } else if (breakdown.features && breakdown.features.length > 0) {
        epics.push({
          title: 'Generated Epic',
          description: '',
          features: breakdown.features.map(f => convertNode(f, 'feature'))
        });
      } else if (breakdown.task) {
        epics.push({
          title: 'Generated Epic',
          description: '',
          tasks: [convertNode(breakdown.task, 'task')]
        });
      } else if (breakdown.tasks && breakdown.tasks.length > 0) {
        epics.push({
          title: 'Generated Epic',
          description: '',
          tasks: breakdown.tasks.map(t => convertNode(t, 'task'))
        });
      } else if (breakdown.subtask) {
        epics.push({
          title: 'Generated Epic',
          description: '',
          subtasks: [convertNode(breakdown.subtask, 'subtask')]
        });
      } else if (breakdown.subtasks && breakdown.subtasks.length > 0) {
        epics.push({
          title: 'Generated Epic',
          description: '',
          subtasks: breakdown.subtasks.map(s => convertNode(s, 'subtask'))
        });
      }
      
      return epics;
    };

    const ticketsPayload = convertToAPIFormat(filterDeleted(JSON.parse(JSON.stringify(localBreakdown))));

    try {
      const result = await dispatch(
        createTickets({
          projectId,
          tickets: { epics: ticketsPayload },
          breakdownHistoryId
        })
      ).unwrap();

      toast.success(t('agent.breakdown.ticketsCreated', { count: result?.data?.total_created || 'Multiple' }));
      
      // Refresh data after ticket creation
      if (onRefresh) {
        await onRefresh();
      }
      
      onTicketsCreated?.(result);
    } catch (err) {
      toast.error(t('agent.breakdown.ticketsCreateFailed'));
      console.error('Failed to create tickets:', err);
    }
  };

  if (!localBreakdown) {
    return null;
  }

  // Check if breakdown data is parseable
  const isValidBreakdown = () => {
    try {
      if (typeof localBreakdown !== 'object') return false;
      // Check if it has at least one valid property
      const hasValidStructure = 
        localBreakdown.epic || 
        localBreakdown.epics || 
        localBreakdown.feature || 
        localBreakdown.features || 
        localBreakdown.task || 
        localBreakdown.tasks || 
        localBreakdown.subtask || 
        localBreakdown.subtasks;
      return !!hasValidStructure;
    } catch (err) {
      return false;
    }
  };

  if (!isValidBreakdown()) {
    return (
      <Box className="breakdown-preview">
        <Box className="agent-section-header">
          <Box className="agent-section-header-content">
            <AutoAwesome className="agent-section-header-icon" />
            <Typography variant="h6" className="agent-section-header-title">
              {t('agent.breakdown.title')}
            </Typography>
          </Box>
        </Box>
        <Paper elevation={2} className="breakdown-preview-paper">
          <Alert severity="error" className="breakdown-preview-error-alert">
            {t('agent.breakdown.parseError')}
          </Alert>
        </Paper>
      </Box>
    );
  }

  const getOutputLevelLabel = () => {
    switch (outputLevel) {
      case AGENT_OUTPUT_LEVELS.EPIC:
        return t('agent.breakdown.epic');
      case AGENT_OUTPUT_LEVELS.FEATURE:
        return t('agent.breakdown.feature');
      case AGENT_OUTPUT_LEVELS.TASK:
        return t('agent.breakdown.task');
      case AGENT_OUTPUT_LEVELS.SUBTASK:
        return t('agent.breakdown.subtask');
      default:
        return t('agent.breakdown.epic');
    }
  };

  return (
    <Box className="breakdown-preview">
      <Box className="agent-section-header">
        <Box className="agent-section-header-content">
          <AutoAwesome className="agent-section-header-icon" />
          <Typography variant="h6" className="agent-section-header-title">
            {t('agent.breakdown.title')}
          </Typography>
          <Chip
            label={`${t('agent.commandBar.outputLevel')}: ${getOutputLevelLabel()}`}
            className="breakdown-preview-output-level-chip"
            sx={{ ml: 2 }}
          />
        </Box>
        <Box className="breakdown-preview-header-right">
          {!ticketsGenerated && !isGlobalEditMode && (
            <Button 
              startIcon={<Edit />} 
              onClick={handleGlobalEditToggle} 
              size="small"
              variant="outlined"
            >
              {t('agent.breakdown.editAll')}
            </Button>
          )}
          {isGlobalEditMode && (
            <>
              <Button 
                startIcon={<Save />} 
                onClick={handleSaveChanges}
                disabled={strategistLoading.updateAgentOutput}
                size="small"
                variant="contained"
              >
                {strategistLoading.updateAgentOutput ? t('agent.breakdown.saving') : t('agent.breakdown.save')}
              </Button>
              <Button 
                startIcon={<Cancel />} 
                onClick={handleGlobalEditCancel}
                size="small"
                variant="outlined"
              >
                {t('agent.breakdown.cancel')}
              </Button>
            </>
          )}
          {onEdit && (
            <Button startIcon={<Edit />} onClick={onEdit} size="small">
              {t('agent.breakdown.edit')}
            </Button>
          )}
        </Box>
      </Box>
      
      <Paper elevation={2} className="breakdown-preview-paper">

      {error && (
        <Alert severity="error" className="breakdown-preview-error-alert">
          {error.message || error}
        </Alert>
      )}

      {insights &&
        (insights.missing_features?.length > 0 ||
          insights.dependencies?.length > 0 ||
          insights.risks?.length > 0) && (
          <Box sx={{ px: 2, pt: 2, pb: 0 }} className="breakdown-insights-panel">
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              {t('agent.breakdown.insightsTitle')}
            </Typography>
            {insights.missing_features?.length > 0 && (
              <Alert severity="warning" icon={<WarningAmber fontSize="inherit" />} sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  {t('agent.breakdown.insightsMissingFeatures')}
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {insights.missing_features.map((item, idx) => (
                    <li key={`mf-${idx}`}>
                      <Typography variant="body2">
                        <strong>{item.title}</strong>
                        {item.reason ? ` — ${item.reason}` : ''}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Alert>
            )}
            {insights.dependencies?.length > 0 && (
              <Alert severity="info" icon={<DeviceHub fontSize="inherit" />} sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  {t('agent.breakdown.insightDependencies')}
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {insights.dependencies.map((item, idx) => (
                    <li key={`dep-${idx}`}>
                      <Typography variant="body2">
                        {item.from} → {item.to}
                        {item.type ? ` (${item.type})` : ''}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Alert>
            )}
            {insights.risks?.length > 0 && (
              <Alert severity="error" icon={<ReportProblem fontSize="inherit" />} sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  {t('agent.breakdown.insightsRisks')}
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {insights.risks.map((item, idx) => (
                    <li key={`risk-${idx}`}>
                      <Typography variant="body2">
                        {item.description}
                        {item.impact ? ` (${item.impact})` : ''}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Alert>
            )}
          </Box>
        )}

      <Box className="breakdown-preview-items-container">
        {renderBreakdownHierarchy(localBreakdown, renderTreeItem)}
      </Box>

      <Box className="breakdown-preview-actions">
        {!ticketsGenerated && (
          <Button
            variant="contained"
            onClick={handleCreateTickets}
            disabled={loading.createTickets}
            startIcon={loading.createTickets ? <CircularProgress size={20} /> : <AddTask />}
            className="create-tickets-button"
          >
            {loading.createTickets ? t('agent.breakdown.creating') : t('agent.breakdown.createTickets')}
          </Button>
        )}
      </Box>
      </Paper>
    </Box>
  );
};

export default BreakdownPreview;
